import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApp } from "../../context/AppContext";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { UserSearchSelect } from "../ui/UserSearchSelect";
import { ItemDocuments } from "../inventory/ItemDocuments";
import { ExtraFieldsEditor, EquipmentForm, buildEquipmentPayload, itemToForm } from "../inventory/EquipmentForm";
import { HandoverSection, emptyHandover, CONDITION_LABELS, CONDITION_OPTIONS, ConditionBadge } from "../inventory/HandoverSection";
import { RebillFields, RebillBadge, RebillActionButton, rebillStatus } from "../inventory/RebillFields";
import { SpecFieldInput } from "../inventory/SpecFieldInput";
import { ReturnDialog } from "../inventory/ReturnDialog";
import { ItemHoldersDialog } from "../inventory/ItemHoldersDialog";
import { QrLabelDialog } from "../inventory/QrLabelDialog";
import {
    ArrowLeft, Package, Save, UserPlus, Undo2, ArrowLeftRight, Wrench,
    Trash2, AlertTriangle, RotateCcw, Clock, QrCode, RefreshCw, FileSignature, Copy, Users
} from "lucide-react";

const STATUS_CONFIG = {
    in_stock: { label: "En stock", color: "bg-emerald-100 text-emerald-700" },
    assigned: { label: "Assigné", color: "bg-blue-100 text-blue-700" },
    maintenance: { label: "Maintenance", color: "bg-orange-100 text-orange-700" },
    retired: { label: "Retiré", color: "bg-gray-200 text-gray-600" },
    lost: { label: "Perdu", color: "bg-red-100 text-red-700" },
    broken: { label: "Endommagé", color: "bg-red-100 text-red-600" },
};

const HISTORY_ICONS = {
    created: "📦", assigned: "👤", returned_to_stock: "↩️", transferred: "🔄",
    maintenance_start: "🔧", maintenance_end: "✅", retired: "🗑️",
    lost: "❌", broken: "💥", modified: "✏️", replaced: "🔁", tag_changed: "🏷️", signed_doc_uploaded: "✍️",
    rebilled: "🧾", rebill_reopened: "🧾",
};

export default function InventoryDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { users, departments, hasPermission, currentUser } = useApp();
    const [item, setItem] = useState(null);
    const [categories, setCategories] = useState([]);
    const [people, setPeople] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editForm, setEditForm] = useState({});
    const [specsForm, setSpecsForm] = useState({});
    const [saving, setSaving] = useState(false);
    const [assignDialog, setAssignDialog] = useState(false);
    const [transferDialog, setTransferDialog] = useState(false);
    const [assignTarget, setAssignTarget] = useState(null);
    const [handover, setHandover] = useState(emptyHandover(null));
    const [photos, setPhotos] = useState([]);
    const [qrOpen, setQrOpen] = useState(false);
    const [returnOpen, setReturnOpen] = useState(false);
    const [holdersOpen, setHoldersOpen] = useState(false);
    const [dupOpen, setDupOpen] = useState(false);
    const [dupForm, setDupForm] = useState(null);
    const [dupBusy, setDupBusy] = useState(false);

    const isAdmin = hasPermission("admin");

    const loadItem = useCallback(async () => {
        try {
            const [data, cats, emps] = await Promise.all([
                api.getInventoryItem(id),
                api.getInventoryCategories(),
                api.getInventoryEmployees(),
            ]);
            setItem(data);
            setCategories(cats);
            setPeople(emps.employees);
            setEditForm({
                name: data.name || "",
                brand: data.brand || "",
                model: data.model || "",
                serial_number: data.serial_number || "",
                asset_tag: data.asset_tag || "",
                condition: data.condition || "",
                notes: data.notes || "",
                purchase_date: data.purchase_date || "",
                purchase_price: data.purchase_price || "",
                purchase_currency: data.purchase_currency || "CHF",
                supplier: data.supplier || "",
                invoice_number: data.invoice_number || "",
                purchase_order_number: data.purchase_order_number || "",
                warranty_start: data.warranty_start || "",
                warranty_end: data.warranty_end || "",
                warranty_type: data.warranty_type || "",
                warranty_notes: data.warranty_notes || "",
                rebill_status: rebillStatus(data),
                rebill_department: data.rebill_department || "",
                rebill_amount: data.rebill_amount || "",
                rebill_date: data.rebill_date || "",
                extra_fields: data.extra_fields || [],
            });
            setSpecsForm(data.specs || {});
        } catch (e) {
            toast.error("Item introuvable");
            navigate("/admin/inventory");
        } finally { setLoading(false); }
    }, [id, navigate]);

    useEffect(() => { loadItem(); }, [loadItem]);

    const userMap = React.useMemo(() => {
        const m = {};
        users.forEach((u) => { m[u.id] = `${u.first_name} ${u.last_name}`; });
        people.forEach((p) => { m[p.id] = `${p.first_name} ${p.last_name}`; });
        return m;
    }, [users, people]);

    const peopleList = React.useMemo(
        () => people.map((p) => ({ id: p.id, first_name: p.first_name, last_name: p.last_name, email: p.email || "", department_id: p.department_id })),
        [people],
    );

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = { ...editForm, specs: specsForm };
            if (payload.purchase_price) payload.purchase_price = parseFloat(payload.purchase_price);
            else payload.purchase_price = null;
            if (payload.rebill_amount) payload.rebill_amount = parseFloat(payload.rebill_amount);
            else payload.rebill_amount = null;
            payload.extra_fields = (payload.extra_fields || []).filter((f) => (f.label || "").trim());
            await api.updateInventoryItem(id, payload);
            toast.success("Sauvegardé");
            loadItem();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    const openAssign = () => {
        setAssignTarget(null);
        setHandover(emptyHandover(currentUser?.id));
        setPhotos([]);
        setAssignDialog(true);
    };

    const handleAssign = async () => {
        if (!assignTarget) return;
        try {
            const res = await api.assignInventoryItem(id, {
                user_id: assignTarget,
                condition: handover.condition,
                condition_notes: handover.condition_notes,
                handover_date: handover.date_undefined ? null : handover.date,
                handover_date_undefined: !!handover.date_undefined,
                handed_over_by: handover.handed_over_by,
            });
            for (const f of photos) {
                await api.uploadInventoryAttachment(id, f, `Photo remise ${handover.date || ""}`.trim(), { category: "etat_des_lieux", historyId: res.history_id });
            }
            toast.success("Assigné");
            setAssignDialog(false);
            setAssignTarget(null);
            loadItem();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleToggleLabeled = async () => {
        try {
            await api.updateInventoryItem(id, { labeled: !item.labeled });
            toast.success(!item.labeled ? "Marqué comme étiqueté" : "Marqué comme non étiqueté");
            loadItem();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleDuplicate = async () => {
        setDupBusy(true);
        try {
            const created = await api.createInventoryItem(buildEquipmentPayload(dupForm));
            toast.success(`"${created.asset_tag || created.name}" créé`);
            setDupOpen(false);
            navigate(`/admin/inventory/${created.id}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setDupBusy(false); }
    };

    const handleTransfer = async () => {
        if (!assignTarget) return;
        try {
            await api.transferInventoryItem(id, { to_user_id: assignTarget });
            toast.success("Transféré");
            setTransferDialog(false);
            setAssignTarget(null);
            loadItem();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleMaintenance = async () => {
        const notes = window.prompt("Raison de la maintenance :");
        if (notes === null) return;
        try { await api.maintenanceInventoryItem(id, { notes }); toast.success("En maintenance"); loadItem(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleRestore = async () => {
        try { await api.restoreInventoryItem(id, {}); toast.success("Remis en stock"); loadItem(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleRetire = async () => {
        if (!window.confirm("Retirer cet item du service ?")) return;
        try { await api.retireInventoryItem(id, {}); toast.success("Retiré"); loadItem(); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleRegenTag = async () => {
        if (!window.confirm(`Régénérer l'Asset Tag de "${item.asset_tag || item.name}" ?\nL'ancien tag sera conservé dans l'historique (traçabilité) et les anciennes étiquettes QR resteront fonctionnelles.`)) return;
        try {
            const updated = await api.regenerateAssetTag(id);
            toast.success(`Nouveau tag : ${updated.asset_tag}`);
            loadItem();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    const handleDelete = async () => {
        if (!window.confirm("Supprimer définitivement cet item ?")) return;
        try { await api.deleteInventoryItem(id); toast.success("Supprimé"); navigate("/admin/inventory"); }
        catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    };

    if (loading) return <div className="page-container"><div className="animate-pulse text-gray-400">Chargement...</div></div>;
    if (!item) return null;

    const catConfig = categories.find((c) => c.key === item.category);
    const sCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.in_stock;
    const photosFor = (hid) => (item.attachments || []).filter((a) => a.history_id === hid);

    return (
        <div className="page-container" data-testid="inventory-detail-page">
            {/* Back + Actions */}
            <div className="flex items-center justify-between mb-4">
                <button onClick={() => navigate("/admin/inventory")} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700" data-testid="back-to-inventory">
                    <ArrowLeft className="w-4 h-4" />Inventaire
                </button>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => setQrOpen(true)} data-testid="qr-label-btn"><QrCode className="w-3.5 h-3.5 mr-1" />Étiquette QR</Button>
                    <Button size="sm" variant="outline" onClick={() => setHoldersOpen(true)} title="Historique des personnes qui ont eu ce matériel" data-testid="holders-btn"><Users className="w-3.5 h-3.5 mr-1" />Détenteurs</Button>
                    <Button size="sm" variant="outline" onClick={() => { setDupForm(itemToForm(item)); setDupOpen(true); }} title="Créer un nouvel item pré-rempli à partir de celui-ci" data-testid="duplicate-item-btn"><Copy className="w-3.5 h-3.5 mr-1" />Dupliquer</Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(api.inventoryHandoverDocUrl(id, "remise"), "_blank")} data-testid="handover-doc-remise-btn"><FileSignature className="w-3.5 h-3.5 mr-1" />Doc. remise</Button>
                    <Button size="sm" variant="outline" onClick={() => window.open(api.inventoryHandoverDocUrl(id, "restitution"), "_blank")} data-testid="handover-doc-restitution-btn"><FileSignature className="w-3.5 h-3.5 mr-1" />Doc. restitution</Button>
                    {item.status === "in_stock" && <Button size="sm" onClick={openAssign} data-testid="action-assign"><UserPlus className="w-3.5 h-3.5 mr-1" />Assigner</Button>}
                    {item.status === "assigned" && <>
                        <Button size="sm" variant="outline" onClick={() => setReturnOpen(true)} data-testid="action-unassign"><Undo2 className="w-3.5 h-3.5 mr-1" />Restituer</Button>
                        <Button size="sm" variant="outline" onClick={() => { setTransferDialog(true); setAssignTarget(null); }} data-testid="action-transfer"><ArrowLeftRight className="w-3.5 h-3.5 mr-1" />Transférer</Button>
                        <Button size="sm" variant="outline" onClick={handleMaintenance} data-testid="action-maintenance"><Wrench className="w-3.5 h-3.5 mr-1" />Maintenance</Button>
                    </>}
                    {item.status === "maintenance" && <Button size="sm" onClick={handleRestore} data-testid="action-restore"><RotateCcw className="w-3.5 h-3.5 mr-1" />Remettre en stock</Button>}
                    <Button size="sm" variant="outline" className="text-orange-600" onClick={handleRetire} data-testid="action-retire"><Trash2 className="w-3.5 h-3.5 mr-1" />Retirer</Button>
                    {isAdmin && <Button size="sm" variant="outline" className="text-red-600" onClick={handleDelete} data-testid="action-delete"><AlertTriangle className="w-3.5 h-3.5 mr-1" />Supprimer</Button>}
                </div>
            </div>

            {/* Header */}
            <div className="card mb-4">
                <div className="card-body">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0"><Package className="w-6 h-6 text-teal-600" /></div>
                        <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${sCfg.color}`}>{sCfg.label}</span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{catConfig?.label || item.category}</span>
                                {(item.condition || item.handover_condition) && <ConditionBadge condition={item.condition || item.handover_condition} testId="item-condition-badge" />}
                                <button onClick={handleToggleLabeled} title="Cliquer pour changer" data-testid="labeled-toggle-btn"
                                    className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium transition-colors ${item.labeled ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-gray-100 text-gray-500 border border-dashed border-gray-300 hover:bg-gray-200"}`}>
                                    <QrCode className="w-3 h-3" />{item.labeled ? "Étiqueté" : "Non étiqueté"}
                                </button>
                            </div>
                            <h1 className="mt-1 text-lg font-bold text-gray-900 font-mono" data-testid="item-title">{item.asset_tag || item.name || "Sans identifiant"}</h1>
                            <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
                                {item.name && item.asset_tag && <span>Nom : {item.name}</span>}
                                {item.serial_number && <span>SN: {item.serial_number}</span>}
                                {item.assigned_to_user_id && (
                                    <span className="text-blue-600">Assigné à : <button onClick={() => navigate(`/admin/inventory/employee/${item.assigned_to_user_id}`)} className="hover:underline">{userMap[item.assigned_to_user_id] || item.assigned_to_user_id}</button></span>
                                )}
                                {item.handover_date_undefined ? (
                                    <span data-testid="item-handover-info">
                                        Remis (date non définie)
                                        {item.handed_over_by && userMap[item.handed_over_by] ? ` par ${userMap[item.handed_over_by]}` : ""}
                                    </span>
                                ) : item.handover_date ? (
                                    <span data-testid="item-handover-info">
                                        Remis le {new Date(item.handover_date).toLocaleDateString("fr-CH")}
                                        {item.handed_over_by && userMap[item.handed_over_by] ? ` par ${userMap[item.handed_over_by]}` : ""}
                                    </span>
                                ) : null}
                                {item.handover_condition && <span className="text-teal-600">État à la remise : {CONDITION_LABELS[item.handover_condition] || item.handover_condition}</span>}
                            </div>
                        </div>
                        <Button onClick={handleSave} disabled={saving} data-testid="save-item-btn"><Save className="w-4 h-4 mr-1" />{saving ? "..." : "Sauvegarder"}</Button>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Left column: details */}
                <div className="lg:col-span-2 space-y-4">
                    {/* General */}
                    <div className="card">
                        <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Détails</h3></div>
                        <div className="card-body grid grid-cols-2 gap-3">
                            <div>
                                <label className="form-label text-xs">Asset Tag (identifiant)</label>
                                <div className="flex gap-1.5">
                                    <Input value={editForm.asset_tag} onChange={(e) => setEditForm({ ...editForm, asset_tag: e.target.value })} className="font-mono" data-testid="edit-tag" />
                                    <Button type="button" variant="outline" size="sm" className="h-9 flex-shrink-0" onClick={handleRegenTag} title="Régénérer un nouveau tag (l'ancien est tracé dans l'historique)" data-testid="regen-tag-btn">
                                        <RefreshCw className="w-3.5 h-3.5 mr-1" />Régénérer
                                    </Button>
                                </div>
                                {(item.previous_tags || []).length > 0 && (
                                    <div className="flex flex-wrap items-center gap-1 mt-1" data-testid="previous-tags">
                                        <span className="text-[10px] text-gray-400">Anciens tags :</span>
                                        {item.previous_tags.map((t) => (
                                            <span key={t} className="text-[9px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono line-through">{t}</span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div><label className="form-label text-xs">Nom (optionnel)</label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name" /></div>
                            <div><label className="form-label text-xs">Marque</label><Input value={editForm.brand} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })} data-testid="edit-brand" /></div>
                            <div><label className="form-label text-xs">Modèle</label><Input value={editForm.model} onChange={(e) => setEditForm({ ...editForm, model: e.target.value })} data-testid="edit-model" /></div>
                            <div><label className="form-label text-xs">N° série</label><Input value={editForm.serial_number} onChange={(e) => setEditForm({ ...editForm, serial_number: e.target.value })} data-testid="edit-serial" /></div>
                            <div><label className="form-label text-xs">État du matériel</label>
                                <select value={editForm.condition || ""} onChange={(e) => setEditForm({ ...editForm, condition: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md" data-testid="edit-condition">
                                    {CONDITION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Specs (category-specific) */}
                    {catConfig?.custom_fields?.length > 0 && (
                        <div className="card">
                            <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Spécifications ({catConfig.label})</h3></div>
                            <div className="card-body grid grid-cols-2 gap-3">
                                {catConfig.custom_fields.map((cf) => (
                                    <div key={cf.key}>
                                        <label className="form-label text-xs">{cf.label}</label>
                                        <SpecFieldInput field={cf} value={specsForm[cf.key]} onChange={(v) => setSpecsForm({ ...specsForm, [cf.key]: v })} testId={`spec-${cf.key}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Champs supplémentaires (au cas par cas) */}
                    <div className="card">
                        <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Champs supplémentaires</h3></div>
                        <div className="card-body">
                            <ExtraFieldsEditor fields={editForm.extra_fields || []} onChange={(v) => setEditForm({ ...editForm, extra_fields: v })} />
                        </div>
                    </div>

                    {/* Purchase */}
                    <div className="card">
                        <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Achat & Fournisseur</h3></div>
                        <div className="card-body grid grid-cols-2 gap-3">
                            <div><label className="form-label text-xs">Date d'achat</label><Input type="date" value={editForm.purchase_date} onChange={(e) => setEditForm({ ...editForm, purchase_date: e.target.value })} data-testid="edit-purchase-date" /></div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><label className="form-label text-xs">Prix</label><Input type="number" step="0.01" value={editForm.purchase_price} onChange={(e) => setEditForm({ ...editForm, purchase_price: e.target.value })} data-testid="edit-price" /></div>
                                <div><label className="form-label text-xs">Devise</label><select value={editForm.purchase_currency} onChange={(e) => setEditForm({ ...editForm, purchase_currency: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md"><option value="CHF">CHF</option><option value="EUR">EUR</option><option value="USD">USD</option></select></div>
                            </div>
                            <div><label className="form-label text-xs">Fournisseur</label><Input value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} data-testid="edit-supplier" /></div>
                            <div><label className="form-label text-xs">N° facture</label><Input value={editForm.invoice_number} onChange={(e) => setEditForm({ ...editForm, invoice_number: e.target.value })} data-testid="edit-invoice" /></div>
                        </div>
                    </div>

                    {/* Warranty */}
                    <div className="card">
                        <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Garantie</h3></div>
                        <div className="card-body grid grid-cols-2 gap-3">
                            <div><label className="form-label text-xs">Début garantie</label><Input type="date" value={editForm.warranty_start} onChange={(e) => setEditForm({ ...editForm, warranty_start: e.target.value })} data-testid="edit-warranty-start" /></div>
                            <div><label className="form-label text-xs">Fin garantie</label><Input type="date" value={editForm.warranty_end} onChange={(e) => setEditForm({ ...editForm, warranty_end: e.target.value })} data-testid="edit-warranty-end" /></div>
                            <div><label className="form-label text-xs">Type</label><Input value={editForm.warranty_type} onChange={(e) => setEditForm({ ...editForm, warranty_type: e.target.value })} placeholder="Standard, Sur site..." data-testid="edit-warranty-type" /></div>
                            <div><label className="form-label text-xs">Notes garantie</label><Input value={editForm.warranty_notes} onChange={(e) => setEditForm({ ...editForm, warranty_notes: e.target.value })} data-testid="edit-warranty-notes" /></div>
                        </div>
                    </div>

                    {/* Refacturation interne */}
                    <div className="card" data-testid="rebill-card">
                        <div className="card-header flex items-center justify-between gap-2">
                            <h3 className="text-sm font-semibold text-gray-800">Refacturation interne <span className="font-normal text-gray-400 text-xs">(optionnel)</span></h3>
                            <div className="flex items-center gap-2">
                                <RebillBadge item={item} testId="detail-rebill-badge" />
                                <RebillActionButton item={item} onChanged={loadItem} />
                            </div>
                        </div>
                        <div className="card-body">
                            <RebillFields form={editForm} set={(k, v) => setEditForm((f) => ({ ...f, [k]: v }))} testPrefix="edit" />
                        </div>
                    </div>

                    {/* Notes */}
                    <div className="card">
                        <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Notes</h3></div>
                        <div className="card-body">
                            <textarea value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="w-full px-3 py-2 text-sm border rounded-md resize-none" rows={3} data-testid="edit-notes" />
                        </div>
                    </div>
                </div>

                {/* Right column: documents + history */}
                <div className="space-y-4">
                    <div className="card">
                        <div className="card-header"><h3 className="text-sm font-semibold text-gray-800">Documents (PDF, factures...)</h3></div>
                        <div className="card-body">
                            <ItemDocuments itemId={id} attachments={item.attachments || []} onChanged={loadItem} itemStatus={item.status} />
                        </div>
                    </div>
                    <div className="card">
                        <div className="card-header flex items-center gap-2"><Clock className="w-4 h-4 text-gray-500" /><h3 className="text-sm font-semibold text-gray-800">Historique</h3></div>
                        <div className="card-body p-0">
                            <div className="px-4 py-2 space-y-3 max-h-[500px] overflow-y-auto">
                                {(item.history || []).map((h) => (
                                    <div key={h.id} className="flex gap-3 py-1 border-b border-gray-50 last:border-0" data-testid={`hist-${h.id}`}>
                                        <span className="text-sm flex-shrink-0">{HISTORY_ICONS[h.action] || "•"}</span>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs text-gray-800">
                                                <span className="font-medium">{formatAction(h, userMap)}</span>
                                            </p>
                                            {h.details?.condition && <p className="text-[10px] text-gray-500">État : {CONDITION_LABELS[h.details.condition] || h.details.condition}</p>}
                                            {h.details?.return_condition && <p className="text-[10px] text-gray-500">État à la restitution : {CONDITION_LABELS[h.details.return_condition] || h.details.return_condition}</p>}
                                            {h.details?.condition_notes && <p className="text-[10px] text-gray-400 italic">{h.details.condition_notes}</p>}
                                            {h.details?.notes && <p className="text-[10px] text-gray-400 mt-0.5">{h.details.notes}</p>}
                                            {photosFor(h.id).length > 0 && (
                                                <div className="flex gap-1 mt-1 flex-wrap">
                                                    {photosFor(h.id).map((p) => (
                                                        <a key={p.id} href={api.inventoryAttachmentUrl(p.id)} target="_blank" rel="noreferrer" title={p.display_name}>
                                                            <img src={api.inventoryAttachmentUrl(p.id)} alt={p.display_name} className="w-10 h-10 object-cover rounded border border-gray-200 hover:opacity-80" />
                                                        </a>
                                                    ))}
                                                </div>
                                            )}
                                            <p className="text-[10px] text-gray-400">{new Date(h.timestamp).toLocaleString("fr-CH")}</p>
                                        </div>
                                    </div>
                                ))}
                                {(!item.history || item.history.length === 0) && <p className="text-xs text-gray-400 py-4 text-center">Aucun historique</p>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Assign Dialog */}
            <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Assigner "{item.asset_tag || item.name}"</DialogTitle></DialogHeader>
                    <div className="max-h-[60vh] overflow-y-auto py-1 pr-1">
                        <div><label className="form-label text-xs">Employé</label><UserSearchSelect users={peopleList} departments={departments} value={assignTarget} onChange={setAssignTarget} placeholder="Sélectionner..." /></div>
                        <HandoverSection value={handover} onChange={setHandover} photos={photos} setPhotos={setPhotos} people={peopleList} departments={departments} mode="remise" />
                    </div>
                    <DialogFooter><Button variant="outline" onClick={() => setAssignDialog(false)}>Annuler</Button><Button onClick={handleAssign} disabled={!assignTarget} data-testid="confirm-assign-btn">Assigner</Button></DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Return Dialog */}
            <ReturnDialog item={returnOpen ? item : null} onClose={() => setReturnOpen(false)} onDone={() => { setReturnOpen(false); loadItem(); }} people={peopleList} departments={departments} />

            {/* Holders history Dialog */}
            <ItemHoldersDialog item={holdersOpen ? item : null} onClose={() => setHoldersOpen(false)} />

            {/* QR Label Dialog */}
            <QrLabelDialog item={item} open={qrOpen} onClose={() => setQrOpen(false)} onLabeled={loadItem} />

            {/* Duplicate Dialog */}
            <Dialog open={dupOpen} onOpenChange={(o) => { if (!o) setDupOpen(false); }}>
                <DialogContent className="max-w-xl">
                    <DialogHeader><DialogTitle>Dupliquer "{item.asset_tag || item.name}"</DialogTitle></DialogHeader>
                    <p className="text-xs text-gray-500">Tous les champs ont été copiés sauf le N° de série et l'Asset Tag. Modifiez ce qui change, puis créez.</p>
                    <div className="py-1 max-h-[60vh] overflow-y-auto pr-1">
                        {dupForm && <EquipmentForm form={dupForm} setForm={setDupForm} categories={categories} allowTemplate={false} />}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDupOpen(false)}>Annuler</Button>
                        <Button onClick={handleDuplicate} disabled={dupBusy} data-testid="confirm-duplicate-btn">{dupBusy ? "..." : "Créer la copie"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Transfer Dialog */}
            <Dialog open={transferDialog} onOpenChange={setTransferDialog}>
                <DialogContent><DialogHeader><DialogTitle>Transférer cet item</DialogTitle></DialogHeader>
                    <div className="py-2"><label className="form-label text-xs">Nouveau destinataire</label><UserSearchSelect users={peopleList.filter((u) => u.id !== item.assigned_to_user_id)} departments={departments} value={assignTarget} onChange={setAssignTarget} placeholder="Sélectionner..." /></div>
                    <DialogFooter><Button variant="outline" onClick={() => setTransferDialog(false)}>Annuler</Button><Button onClick={handleTransfer} disabled={!assignTarget} data-testid="confirm-transfer-inv-btn">Transférer</Button></DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function formatAction(h, userMap) {
    const actor = userMap[h.actor_id] || "Système";
    const from = h.from_user_id ? userMap[h.from_user_id] || "?" : "";
    const to = h.to_user_id ? userMap[h.to_user_id] || "?" : "";
    switch (h.action) {
        case "created": return `Créé par ${actor}`;
        case "assigned": {
            const remisPar = userMap[h.details?.handed_over_by] || actor;
            const dateInfo = h.details?.handover_date_undefined ? " — date de remise non définie" : "";
            return h.details?.replaces_item_name
                ? `Assigné à ${to} en remplacement de "${h.details.replaces_item_name}" — remis par ${remisPar}${dateInfo}`
                : `Assigné à ${to} — remis par ${remisPar}${dateInfo}`;
        }
        case "returned_to_stock": {
            const dateInfo = h.details?.return_date_undefined
                ? " — date non définie"
                : (h.details?.return_date ? ` le ${new Date(h.details.return_date).toLocaleDateString("fr-CH")}` : "");
            const rbName = h.details?.returned_by ? (userMap[h.details.returned_by] || "?") : null;
            if (rbName) return `Restitué${dateInfo} — rendu par ${rbName} (via ${actor})`;
            return from ? `Restitué par ${from}${dateInfo} (via ${actor})` : `Remis en stock par ${actor}${dateInfo}`;
        }
        case "transferred": return `Transféré de ${from} à ${to} par ${actor}`;
        case "maintenance_start": return `En maintenance (${actor})`;
        case "retired": return `Retiré du service par ${actor}`;
        case "lost": return `Marqué perdu par ${actor}`;
        case "broken": return `Marqué endommagé par ${actor}`;
        case "modified": return `Modifié par ${actor}`;
        case "replaced": return `Remplacé par "${h.details?.replacement_item_name || "un autre matériel"}" (${actor})`;
        case "tag_changed": return `Asset Tag modifié : ${h.details?.old_tag || "—"} → ${h.details?.new_tag} (${h.details?.mode === "regeneration" ? "régénéré" : "modifié manuellement"} par ${actor})`;
        case "rebilled": return `Refacturation validée${h.details?.department ? ` — ${h.details.department}` : ""}${h.details?.amount ? ` (${h.details.amount} CHF)` : ""} par ${actor}`;
        case "rebill_reopened": return `Refacturation remise à faire par ${actor}`;
        case "signed_doc_uploaded": return `${h.details?.doc_type === "restitution_signee" ? "Restitution signée" : "Remise signée"} déposée — signée le ${h.details?.signed_date ? new Date(h.details.signed_date).toLocaleDateString("fr-CH") : "—"} (par ${actor})`;
        default: return `${h.action} par ${actor}`;
    }
}
